// A block's media is a chain of segments, each covering a run of the block's scenes (from its `from`
// scene up to the next segment's):
//   {kind: 'video', from, video, duration, hold?, overlap?}  a stage video; the show moves on when it ends (+ hold of black)
//   {kind: 'song',  from, clips: [clipId…], hold?, overlap?} a song (or medley); its scenes are pinned to points in it
//   {kind: 'bed',   from, clips: [clipId…], enterAt?, crossfade?}
//        open-ended: its scenes wait for the game; the playlist loops until the show moves on, then fades out.
//        enterAt (seconds into the previous song) starts the bed there and lets that song play out; the
//        playlist comes in `crossfade` seconds before the song ends.
// A song's or bed's clips play back to back, or crossfade: `overlaps[n]` seconds between clip n and n + 1.
// A segment starts when the previous one ends, `overlap` seconds earlier if set; it may start on the same scene as
// the previous one, adding sound under that scene without a scene of its own. A scene's `at` is seconds into
// its segment (a segment's first scene defaults to its start). Block time after a bed assumes one pass of its
// playlist; the engine starts what follows a bed when the game moves on. block.musicEnd: 'stop' | 'loop' | 'next'.
// This file is shared verbatim with the editor (client/src/editor/segments.js).
export const DEFAULT_VIDEO_HOLD = 1;
export const TAIL_SCENE_SECONDS = 6;

export const layoutBlock = (doc, block, {tailSeconds = TAIL_SCENE_SECONDS} = {}) => {
    const segs = block.segments || [];
    if (!segs.length) return null;
    const clipsById = doc.clips || {};
    const segments = [];
    segs.forEach((s, k) => {
        const prev = segments[k - 1];
        const to = k + 1 < segs.length ? segs[k + 1].from : block.scenes.length;
        const clips = s.kind === 'video' ? [] : (s.clips || []).map(id => clipsById[id]).filter(Boolean);
        const overlaps = s.overlaps || [];
        const clipsLength = clips.reduce((n, c) => n + c.end - c.start, 0) - clips.slice(1).reduce((n, c, i) => n + (+overlaps[i] || 0), 0);
        const prevEnd = prev ? prev.start + prev.dur : 0;
        if (s.kind === 'bed') {
            const start = prev && s.enterAt != null ? prev.start + Math.min(+s.enterAt, prev.length) : prevEnd;
            const playlistAt = prev && s.enterAt != null ? Math.max(start, prev.start + prev.length - (+s.crossfade || 0)) : start;
            const length = playlistAt - start + clipsLength;
            segments.push({...s, k, to, clips, start, playlistAt, length, hold: 0, dur: length, open: true});
            return;
        }
        const length = s.kind === 'video' ? +s.duration || 0 : clipsLength;
        const hold = s.hold ?? (s.kind === 'video' ? DEFAULT_VIDEO_HOLD : 0);
        segments.push({...s, k, to, clips, start: Math.max(0, prevEnd - (+s.overlap || 0)), length, hold, dur: length + hold});
    });
    const total = Math.max(...segments.map(g => g.start + g.dur));
    // Several segments may start on one scene (a song coming in under the end of a video): the scene goes with the first.
    const segmentOf = (i) => segments.reduce((cur, s) => (s.from <= i && s.from > cur.from ? s : cur), segments[0]);
    // Scenes the last segment can't place (untimed, or past its end) go in a tail after it, a nominal length each.
    // A bed's scenes after its first wait for the game: shown spread across the bed, untimed.
    let tail = 0;
    const starts = [], timed = [];
    block.scenes.forEach((scene, i) => {
        const seg = segmentOf(i), last = seg === segments.at(-1);
        if (seg.open) {
            const n = seg.to - seg.from, j = i - seg.from;
            starts.push(seg.start + seg.dur * j / n); timed.push(j === 0);
            return;
        }
        const at = i === seg.from ? (scene.at ?? 0) : scene.at;
        if (!tail && at != null && (at < seg.dur - 0.01 || (!last && at <= seg.dur))) { starts.push(seg.start + at); timed.push(true); return; }
        if (last) { starts.push(total + tailSeconds * tail++); timed.push(false); return; }
        starts.push(seg.start + seg.dur); timed.push(true);
    });
    const duration = tail ? total + tailSeconds * tail : total;
    // Everything that sounds, in block time: song and bed clips back to back, and each video's own audio.
    const audio = segments.flatMap(seg => {
        if (seg.kind === 'video') return [{kind: 'video', seg: seg.k, video: seg.video, at: seg.start, length: seg.length}];
        let o = seg.open ? seg.playlistAt : seg.start;
        return seg.clips.map((clip, n) => {
            const item = {kind: 'clip', seg: seg.k, n, clip, at: o, length: clip.end - clip.start, bed: !!seg.open};
            o += item.length - (+(seg.overlaps || [])[n] || 0);
            return item;
        });
    });
    return {segments, starts, timed, total, duration, audio, segmentOf};
};

// Song and bed clips in block order (the editor and the trim route address a clip by this index).
export const songClips = (layout) => (layout ? layout.audio.filter(a => a.kind === 'clip') : []);

// The first bed still ahead of block time t (or under way at t): playback runs up to it and waits there.
export const bedAhead = (layout, t) => layout.segments.find(g => g.open && g.start + g.dur > t + 1e-6) || null;
